import React, { ReactNode } from 'react';

import ErrorIcon from '@mui/icons-material/ErrorOutline';
import { Variant } from '@mui/material/styles/createTypography';
import { SvgIconProps, useTheme } from '@mui/material';

import { Box, BoxColors, SpacingValues } from '../Box';
import { Typography } from '../Typography';

import { useStyles } from './Callout.styles';

type CalloutVariant = 'error' | 'warning' | 'primary' | 'paper';
type Size = 'small' | 'medium' | 'large' | 'none';

/**
 * Интерфейс, представляющий свойства для компонента Callout.
 *
 * @interface CalloutProps
 * @property {boolean} [bordered=false] - Нужно ли отображать границу для каллоута.
 * @property {ReactNode | ReactNode[]} children - Содержимое, которое будет отображено внутри каллоута.
 * @property {boolean} [showIcon=true] - Нужно ли отображать иконку в каллоуте.
 * @property {'error' | 'warning' | 'primary' | 'paper'} [variant='primary'] - Вариант каллоута.
 * @property {boolean} [withBackground=true] - Нужно ли отображать фоновый цвет для каллоута.
 * @property {boolean} [expanded=false] - Нужно ли разворачивать каллоут для отображения дополнительного содержимого.
 * @property {string} [title] - Заголовок, который будет отображен в каллоуте.
 * @property {'small' | 'medium' | 'large'} [size='medium'] - Размер каллоута.
 */

export interface CalloutProps {
  bordered?: boolean;
  children: ReactNode | ReactNode[];
  showIcon?: boolean;
  variant?: CalloutVariant;
  withBackground?: boolean;
  expanded?: boolean;
  title?: string;
  size?: Size;
}

/**
 * Компонент для отображения каллоута с опциональной границей, фоновым цветом и иконкой.
 * Поддерживает различные варианты и размеры, а также может быть развернут для отображения дополнительного содержимого.
 *
 * @component
 * @param {CalloutProps} props - Свойства для компонента Callout.
 * @param {boolean} [props.bordered=false] - Нужно ли отображать границу для каллоута.
 * @param {ReactNode | ReactNode[]} props.children - Содержимое, которое будет отображено внутри каллоута.
 * @param {boolean} [props.showIcon=true] - Нужно ли отображать иконку в каллоуте.
 * @param {'error' | 'warning' | 'primary' | 'paper'} [props.variant='primary'] - Вариант каллоута.
 * @param {boolean} [props.withBackground=true] - Нужно ли отображать фоновый цвет для каллоута.
 * @param {boolean} [props.expanded=false] - Нужно ли разворачивать каллоут для отображения дополнительного содержимого.
 * @param {string} [props.title] - Заголовок, который будет отображен в каллоуте.
 * @param {'small' | 'medium' | 'large'} [props.size='medium'] - Размер каллоута.
 * @returns {JSX.Element} Компонент Callout.
 */

export const Callout = ({
  bordered = false,
  children,
  title,
  showIcon = true,
  withBackground = true,
  variant = 'primary',
  expanded = false,
  size = 'medium',
}: CalloutProps) => {
  const { classes } = useStyles();
  const theme = useTheme();

  const borderColor: Record<CalloutVariant, BoxColors> = {
    error: 'accent.text.error',
    warning: 'accent.text.warning',
    primary: 'stroke.primary',
    paper: 'stroke.primary',
  };

  const bgColor: Record<CalloutVariant, BoxColors> = {
    error: 'accent.bg.error',
    warning: 'accent.bg.warning',
    primary: 'bg.greyscale.primary',
    paper: 'bg.light.primary',
  };

  const color: Record<CalloutVariant, BoxColors> = {
    error: 'accent.text.error',
    warning: 'accent.text.warning',
    primary: 'text.light.primary',
    paper: 'text.light.primary',
  };

  const iconColor: Record<CalloutVariant, SvgIconProps['color']> = {
    error: 'inherit',
    warning: 'inherit',
    primary: 'primary',
    paper: 'primary',
  };

  const expandedPaddingBySize: Record<Size, SpacingValues> = {
    small: '16',
    medium: '24',
    large: '32',
    none: '0',
  };

  const paddingXBySize: Record<Size, SpacingValues> = {
    small: '12',
    medium: '16',
    large: '24',
    none: '0',
  };
  const paddingYBySize: Record<Size, SpacingValues> = {
    small: '4',
    medium: '8',
    large: '16',
    none: '0',
  };

  const typographyTitleVariant: Record<Size, Variant> = {
    small: 'subtitle2',
    medium: 'subtitle1',
    large: 'h6',
    none: 'body2',
  };

  return (
    <Box
      bgcolor={withBackground ? bgColor[variant] : undefined}
      radius={size === 'large' ? 'l' : 'm'}
      border={bordered ? '1px solid' : 'none'}
      borderColor={withBackground ? borderColor[variant] : borderColor.primary}
      color={withBackground ? color[variant] : color.primary}
      className="aqa_callout"
      testId="aqa_callout"
      px={expanded ? expandedPaddingBySize[size] : paddingXBySize[size]}
      py={expanded ? expandedPaddingBySize[size] : paddingYBySize[size]}
    >
      {expanded ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="center"
        >
          <Box display="flex" alignItems="center" mb="8">
            {showIcon && (
              <Box
                mr={size === 'small' ? '8' : '12'}
                display="flex"
                alignItems="center"
                alignSelf="flex-start"
                color={color[variant]}
              >
                <ErrorIcon
                  color={iconColor[variant]}
                  className={classes.icon}
                  data-testid="icon"
                />
              </Box>
            )}
            {title && (
              <Typography variant={typographyTitleVariant[size]}>
                {title}
              </Typography>
            )}
          </Box>
          <Box
            display="flex"
            alignItems="center"
            fontSize={
              size === 'large'
                ? theme.tokens.typography.variant.body2.fontSize
                : theme.tokens.typography.variant.body3.fontSize
            }
          >
            {children}
          </Box>
        </Box>
      ) : (
        <Box display="flex" alignItems="flex-start">
          {showIcon && (
            <Box
              mr={size === 'small' ? '8' : '12'}
              color={color[variant]}
              display="flex"
              alignItems="center"
            >
              <ErrorIcon color={iconColor[variant]} className={classes.icon} />
            </Box>
          )}
          {children}
        </Box>
      )}
    </Box>
  );
};
